import React, { ElementType, ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Divider, Grid, Typography, SxProps, Theme } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project import
import MainCard from 'components/MainCard';
import navigation from 'menu-items';
import { ThemeDirection } from 'config';

// assets
import { ApartmentOutlined, HomeOutlined, HomeFilled } from '@ant-design/icons';
import { SvgIconComponent } from '@mui/icons-material';

// ==============================|| BREADCRUMBS ||============================= //

interface BreadcrumbsProps {
  card?: boolean;
  custom?: boolean;
  divider?: boolean;
  icon?: boolean;
  icons?: boolean;
  maxItems?: number;
  rightAlign?: boolean;
  separator?: React.ElementType;
  title?: boolean;
  titleBottom?: boolean;
  sx?: SxProps<Theme>;
}

interface MenuItemType {
  title?: string;
  url?: string;
  type?: 'group' | 'collapse' | 'item';
  children?: MenuItemType[];
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  card = false,
  custom = false,
  divider = false,
  icon,
  icons,
  maxItems,
  rightAlign,
  separator,
  title = true,
  titleBottom = true,
  sx,
  ...others
}) => {
  const theme = useTheme();
  const location = useLocation();
  const [main, setMain] = useState<MenuItemType | undefined>();
  const [item, setItem] = useState<MenuItemType | undefined>();

  const iconSX = {
    marginRight: theme.direction === ThemeDirection.RTL ? '0' : theme.spacing(0.75),
    marginLeft: theme.direction === ThemeDirection.RTL ? theme.spacing(0.75) : '0',
    marginTop: `-${theme.spacing(0.25)}`,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  const customLocation = location.pathname;

  useEffect(() => {
    navigation?.items?.forEach((menu: MenuItemType) => {
      if (menu.type === 'group') {
        if (menu?.url === customLocation) {
          setMain(menu);
          setItem(menu);
        } else {
          getCollapse(menu);
        }
      }
    });
  }, [customLocation]);

  const getCollapse = (menu: MenuItemType) => {
    if (!custom && menu.children) {
      menu.children.forEach((collapse) => {
        if (collapse.type === 'collapse') {
          getCollapse(collapse);
          if (collapse.url === customLocation) {
            setMain(collapse);
            setItem(collapse);
          }
        } else if (collapse.type === 'item' && collapse.url === customLocation) {
          setMain(menu);
          setItem(collapse);
        }
      });
    }
  };

  const SeparatorIcon = separator;
  const separatorIcon = separator ? React.createElement(separator as ElementType, { style: { fontSize: '0.75rem', marginTop: 2 } }) : '/';

  const breadcrumbItems: React.ReactNode[] = [];
  const paths = customLocation.split('/').filter(Boolean);
  let pathURL = '';

  paths.forEach((path, index) => {
    pathURL += `/${path}`;
    const isCurrentPage = index === paths.length - 1;
    const label = path
      .replace(/-/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    breadcrumbItems.push(
      isCurrentPage ? (
        <Typography key={path} variant="subtitle1" color="textPrimary">
          {icons && <ApartmentOutlined style={iconSX} />}
          {label}
        </Typography>
      ) : (
        <Typography key={path} component={Link} to={pathURL} variant="subtitle1" sx={{ textDecoration: 'none', color: 'text.primary' }}>
          {icons && <ApartmentOutlined style={iconSX} />}
          {label}
        </Typography>
      )
    );
  });

  return (
    <MainCard
      border={card}
      sx={card === false ? { mb: 3, bgcolor: 'transparent', ...sx } : { mb: 3, ...sx }}
      {...others}
      content={card}
      shadow="none"
    >
      <Grid
        container
        direction={rightAlign ? 'row' : 'column'}
        justifyContent={rightAlign ? 'space-between' : 'flex-start'}
        alignItems={rightAlign ? 'center' : 'flex-start'}
        spacing={1}
      >
        <Grid>
          <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
            <Typography component={Link} to="/" color="textSecondary" variant="h6" sx={{ textDecoration: 'none' }}>
              {icons && <HomeOutlined style={iconSX} />}
              {icon && !icons && <HomeFilled style={{ ...iconSX, marginRight: 0 }} />}
              {(!icon || icons) && 'Home'}
            </Typography>
            {breadcrumbItems}
          </MuiBreadcrumbs>
        </Grid>
        {title && titleBottom && (
          <Grid sx={{ mt: card === false ? 0.25 : 1 }}>
            <Typography variant="h2">{item?.title}</Typography>
          </Grid>
        )}
      </Grid>
      {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
    </MainCard>
  );
};

export default Breadcrumbs;
